import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import { Button, AutoComplete, Card, theme } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useQuery } from "@apollo/client";
import { GET_USER_EVENTS, GET_USER_ROLES_BY_EVENT, GET_PLACES_BY_EVENT } from "../graphql/queries";
import LocationAccuracyModal from "../components/LocationAccuracyModal";
import AnimatedLogo from "../components/AnimatedLogo";
import { Location, PlaceWithDistance, Role, Event } from "../types";
import { useNavigate } from "react-router-dom";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { useAppTheme } from "../theme/ThemeProvider";

// Fix for default marker icons in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const placeMarkerSize = { width: 25, height: 41, anchorX: 12, anchorY: 41 };

const placeMarkerDivIcon = L.divIcon({
  html: `<img
    src="${markerIcon}"
    style="
      width:${placeMarkerSize.width}px;
      height:${placeMarkerSize.height}px;
    "
  />`,
  iconSize: [placeMarkerSize.width, placeMarkerSize.height],
  iconAnchor: [placeMarkerSize.anchorX, placeMarkerSize.anchorY],
  popupAnchor: [0, -placeMarkerSize.height + 6],
  className: "place-marker-div",
});

interface PlacesMapProps {
  places: PlaceWithDistance[];
  setPlacesForMap?: (places: PlaceWithDistance[]) => void;
  setUserRolesDetail?: (roles: Role[]) => void;
  setPlaceDetail: (place: PlaceWithDistance | null) => void;
  placeDetail: PlaceWithDistance | null;
  location: Location | null;
  setLocation: (location: Location | null) => void;
}

function MapController({
  location,
  flyToPlace,
  onFlyComplete,
}: {
  location: Location | null;
  flyToPlace: PlaceWithDistance | null;
  onFlyComplete: () => void;
}) {
  const map = useMap();

  useEffect(() => {
    if (location) {
      map.setView([location.latitude, location.longitude], 15);
    }
  }, [location, map]);

  useEffect(() => {
    if (!flyToPlace?.geo_location?.latitude || !flyToPlace?.geo_location?.longitude) return;
    const lat = flyToPlace.geo_location!.latitude!;
    const lng = flyToPlace.geo_location!.longitude!;
    map.setView([lat, lng], 17);
    onFlyComplete();
  }, [flyToPlace, map, onFlyComplete]);

  return null;
}

export default function PlacesMap({
  places: placesFromProps,
  setPlacesForMap,
  setUserRolesDetail,
  setPlaceDetail,
  placeDetail,
  location,
  setLocation,
}: PlacesMapProps) {
  const navigate = useNavigate();
  const { themeName } = useAppTheme();
  const { token } = theme.useToken();
  const [showAccuracyModal, setShowAccuracyModal] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [flyToPlace, setFlyToPlace] = useState<PlaceWithDistance | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [selectedPlace, setSelectedPlace] = useState<PlaceWithDistance | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      debounceRef.current = null;
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  const onFlyComplete = useCallback(() => setFlyToPlace(null), []);

  const userId = localStorage.getItem("userId") || "";
  const eventId = localStorage.getItem("eventId") || "";

  const { data: eventsData } = useQuery<{ getUserEvents: { events: Event[]; success: boolean } }>(
    GET_USER_EVENTS,
    { variables: { input: { userId } }, skip: !userId }
  );

  const hasEvent = eventId && (eventsData?.getUserEvents?.events || []).some((e) => e.id === eventId);

  const { data: rolesData } = useQuery<{ getUserRolesByEvent: { roles: Role[]; success: boolean } }>(
    GET_USER_ROLES_BY_EVENT,
    {
      variables: { input: { userId, eventId } },
      skip: !userId || !eventId || !location,
    }
  );

  const placesQueryEnabled = Boolean(userId && eventId && location);
  const { data: placesData, loading: loadingPlaces } = useQuery<{
    getPlacesByEvent: { places: PlaceWithDistance[]; success: boolean };
  }>(GET_PLACES_BY_EVENT, {
    variables: {
      input: {
        userId,
        eventId,
        latitude: location?.latitude ?? 0,
        longitude: location?.longitude ?? 0,
      },
    },
    skip: !placesQueryEnabled,
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (rolesData?.getUserRolesByEvent?.roles) {
      const roleIds = rolesData.getUserRolesByEvent.roles.map((r) => r.id);
      localStorage.setItem("userRoles", JSON.stringify(roleIds));
      setUserRolesDetail?.(rolesData.getUserRolesByEvent.roles);
    }
  }, [rolesData, setUserRolesDetail]);

  useEffect(() => {
    if (placesData?.getPlacesByEvent?.places && setPlacesForMap) {
      setPlacesForMap(placesData.getPlacesByEvent.places);
    }
  }, [placesData, setPlacesForMap]);

  const handleAcceptLocation = (coords: GeolocationCoordinates) => {
    setLocation({ latitude: coords.latitude, longitude: coords.longitude });
    setShowAccuracyModal(false);
  };

  const handleAutoAccept = (coords: GeolocationCoordinates) => {
    setLocation({ latitude: coords.latitude, longitude: coords.longitude });
    setShowAccuracyModal(false);
  };

  const places = placesData?.getPlacesByEvent?.places ?? placesFromProps;

  const searchOptions = useMemo(() => {
    if (!debouncedSearch) return [];
    const q = debouncedSearch.toLowerCase();
    return (places || [])
      .filter(
        (p) =>
          (p.name && p.name.toLowerCase().includes(q)) ||
          (p.loc_name && p.loc_name.toLowerCase().includes(q))
      )
      .map((place) => ({
        value: place.id,
        label: place.name || place.loc_name || place.id,
        place,
      }));
  }, [places, debouncedSearch]);

  const defaultCenter: [number, number] = location
    ? [location.latitude, location.longitude]
    : [50.0353, 15.7689];

  const handleGoToDetail = (place: PlaceWithDistance) => {
    setPlaceDetail(place);
    setSelectedPlace(null);
    navigate(`/place/${place.id}`);
  };

  if (!location) {
    return (
      <>
        <LocationAccuracyModal
          visible={showAccuracyModal}
          onAccept={handleAcceptLocation}
          onAutoAccept={handleAutoAccept}
        />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <AnimatedLogo loading="spinner" />
        </div>
      </>
    );
  }

  if (!eventId || !hasEvent) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          padding: 24,
          textAlign: "center",
        }}
      >
        <p>Vyberte prosím akci v menu (ikona ☰), poté se na mapě zobrazí stánky.</p>
      </div>
    );
  }

  if (loadingPlaces) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <AnimatedLogo loading="spinner" />
      </div>
    );
  }

  return (
    <div style={{ width: "100%", height: "100vh", position: "relative" }}>
      <div
        style={{
          position: "absolute",
          top: 16,
          left: "52%",
          transform: "translateX(-50%)",
          zIndex: 1000,
          width: "90%",
        }}
      >
        <AutoComplete
          value={searchInput}
          onChange={setSearchInput}
          options={searchOptions}
          style={{ width: "100%",height: "50px" }}
          size="large"
          placeholder="Hledat místo..."
          onSelect={(value) => {
            const opt = searchOptions.find((o) => o.value === value);
            if (opt?.place) {
              // Показываем то же кастомное окно, что и при клике по пину
              setSelectedPlace(opt.place);
              setFlyToPlace(opt.place);
              setSearchInput("");
            }
          }}
          allowClear
        />
      </div>
      <MapContainer
        center={defaultCenter}
        zoom={15}
        style={{ height: "100%", width: "100%" }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          // Dark tiles for dark mode
          url={
            themeName === "dark"
              ? "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
              : "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          }
          key={`tile-${themeName}`}
        />
        <MapController
          location={location}
          flyToPlace={flyToPlace}
          onFlyComplete={onFlyComplete}
        />

        <Marker
          position={[location.latitude, location.longitude]}
          icon={L.divIcon({
            className: "custom-user-marker",
            html: `<div style="
              width: 20px;
              height: 20px;
              background-color: #1890ff;
              border: 3px solid white;
              border-radius: 50%;
              box-shadow: 0 2px 8px rgba(0,0,0,0.3);
            "></div>`,
            iconSize: [20, 20],
            iconAnchor: [10, 10],
          })}
        />

        {(places || [])
          .filter((place) => place.geo_location?.latitude && place.geo_location?.longitude)
          .map((place) => (
            <Marker
              key={place.id}
              position={[
                place.geo_location!.latitude!,
                place.geo_location!.longitude!,
              ]}
              icon={placeMarkerDivIcon}
              eventHandlers={{
                click: () => setSelectedPlace(place),
              }}
            />
          ))}
      </MapContainer>

      {selectedPlace && (
        <Card
          style={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            bottom: 96,
            width: "92%",
            borderRadius: 16,
            background: token.colorBgElevated,
            border: `1px solid ${token.colorBorderSecondary}`,
            zIndex: 1200,
          }}
        >
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, paddingRight: 12 }}>
            <div
              style={{
                width: 54,
                height: 54,
                borderRadius: 12,
                background: themeName === "dark" ? "rgba(22,212,224,0.12)" : "rgba(22,212,224,0.10)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flex: "0 0 auto",
                border: `1px solid ${themeName === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)"}`,
              }}
            >
              <span style={{ fontSize: 22 }}>📍</span>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 18, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {selectedPlace.name}
              </div>
              {selectedPlace.distance != null && (
                <div style={{ marginTop: 4, color: token.colorTextSecondary, fontSize: 12 }}>
                  {selectedPlace.distance >= 1000
                    ? `${(selectedPlace.distance / 1000).toFixed(2)} km`
                    : `${Math.round(selectedPlace.distance)} m`}
                </div>
              )}
            </div>
            </div>

            <Button
              type="text"
              aria-label="Close"
              onClick={() => setSelectedPlace(null)}
              style={{
                color: token.colorText,
                fontSize: 18,
                lineHeight: "18px",
                padding: 0,
              }}
            >
              <CloseOutlined />
            </Button>
          </div>

          <div style={{ marginTop: 14, display: "flex", gap: 12 }}>
            <Button
              type="primary"
              block
              onClick={() => handleGoToDetail(selectedPlace)}
              style={{
                height: 54,
                fontWeight: 700,
                borderRadius: 12,
              }}
            >
              Přejít na detail
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
